/**
 * LookSystem — renderer config, colour space, tonemapping, exposure, the lighting rig,
 * shadows, sky, fog, weather visuals and the post stack. SINGLE SEQUENTIAL OWNER (N10).
 *
 * OWNER: `look`. Nobody else touches a colour, a light, a shader or a render call.
 * ARCHITECTURE.md §2 frame order step 10: `look.render()` is the ONLY place rendering happens.
 *
 * ── The art-direction argument this file implements ────────────────────────────────────
 *
 * READABILITY IS THE JOB. A player at 90 km/h has to find the next pothole in under 0.4 s.
 * That is a VALUE problem, not a detail problem, so the whole rig is built around three
 * separations that must survive every weather:
 *
 *     road          the brightest large surface in frame   (asphalt L* 66)
 *     buildings     a clear step darker                    (L* 40-58)
 *     cones/holes   the highest-contrast objects in frame  (cone body with L* 92 bands,
 *                                                           open hole L* 13)
 *
 * TONEMAPPING is chosen against that. ACESFilmic desaturates highlights hard and turns the
 * sampled Crazy Taxi sky (#3473FB) into washed cyan; AgX is flatter still. We use Khronos
 * PBR Neutral, which is near-identity below 0.8 linear — so the palette comes out of the
 * pipe as authored — and then the grade adds a saturation lift and a small S-curve. That is
 * where the arcade confidence comes from: honest colour, confident grade, not a crushed
 * filmic curve fighting a hand-tuned palette.
 *
 * SHADOWS are hard and directional. Look at any Crazy Taxi still: the taxi throws a definite
 * dark blob and the buildings throw crisp diagonals. Ambient mush reads as fog, not as
 * sunlight, and it destroys the ground-contact cue you need to judge where a pothole is.
 *
 * FOG is linear and starts a long way out (145 m in thaw). Exponential fog eats the
 * mid-ground, and the mid-ground is exactly where the next objective lives.
 *
 * ── Determinism ────────────────────────────────────────────────────────────────────────
 * Every clock in here is `ctx.time.sim`. Cloud drift, precipitation phase and the weather
 * cross-fade are pure functions of it, so `freezeClocks()` only has to collapse in-flight
 * transitions; the rest is already reproducible (§7.3).
 */

import * as THREE from 'three';
import { LOOK_TUNING as L } from './tuning.js';
import { Sky } from './sky.js';
import { PostStack } from './post.js';
import { WEATHER_PROFILES, blendProfiles, makeBlendTarget, Precipitation } from './weather.js';
import { FallbackStage } from './fallbackStage.js';
import { LampPools } from './lamps.js';
import { EV } from '../core/bus.js';
import { clamp01, smoothstep } from '../core/mathx.js';
import { LIGHT } from '../materials/palette.js';

const DEG = Math.PI / 180;

export class LookSystem {
  static id = 'look';

  constructor(ctx) {
    this.ctx = ctx;
    this.id = 'look';

    this.sky = null;
    this.post = null;
    this.precip = null;
    this.fallbackStage = null;
    this.lampPools = null;

    this.sun = null;
    this.fill = null;
    this.hemi = null;
    this.sodium = null;
    this.headlights = null;

    /** Weather cross-fade state. All in sim seconds. */
    this.weather = 'thaw';
    this.weatherFrom = 'thaw';
    this.transitionStart = 0;
    this.transitionDur = 0;

    /** The live blended profile — mutated in place, never reallocated. */
    this.profile = makeBlendTarget();
    this._fromProfile = null;

    this._frozen = false;
    this._postEnabled = true;
    this._envRT = null;
    this._pmrem = null;
    this._envKey = '';
    this._envBuilds = 0;

    this._scratchV = new THREE.Vector3();
    this._scratchV2 = new THREE.Vector3();
    this._scratchC = new THREE.Color();
    this._shadowFocus = new THREE.Vector3();
    this.sunDirection = new THREE.Vector3(0.45, 0.72, 0.52);

    this._offWeather = null;
    /** `_groundY` cache — see the note there. Keyed on sim time, so it stays deterministic. */
    this._groundYCache = 0;
    this._groundYAt = -2;
    this.timeOfDay = 0.42;
    this._exposure = L.renderer.exposure;
  }

  /* ══════════════════════════════════════════════════════════════════════════ init ══ */

  async init() {
    const ctx = this.ctx;
    const rng = ctx.rng.fork('look');

    this._configureRenderer();

    // ── sky ──────────────────────────────────────────────────────────────────────────
    this.sky = new Sky();
    ctx.scene.add(this.sky.mesh);
    // Publish the sky material through the library so nobody else ever builds one.
    if (ctx.materials && typeof ctx.materials._installSky === 'function') {
      ctx.materials._installSky(this.sky.material);
    }

    // ── lighting rig ─────────────────────────────────────────────────────────────────
    // Every light exists for the whole session and is dimmed to zero rather than removed.
    // Adding or removing a light changes NUM_*_LIGHTS, which recompiles every shader in the
    // scene — a guaranteed frame hitch on every weather change if you get this wrong.
    this._buildLights();

    // ── fog ──────────────────────────────────────────────────────────────────────────
    ctx.scene.fog = new THREE.Fog(0x8DC3FC, L.fog.near.thaw, L.fog.far.thaw);
    ctx.scene.background = null;   // the dome covers the whole frame

    // ── post ─────────────────────────────────────────────────────────────────────────
    // Debug overrides, presentation-only and never read by simulation: `?post=0`,
    // `?msaa=N`, `?shadows=0`. They exist so the look can be A/B'd in one build.
    const q = new URLSearchParams(location.search);
    this._postEnabled = ctx.config.quality.post !== false && q.get('post') !== '0';
    if (q.get('shadows') === '0') ctx.renderer.shadowMap.enabled = false;
    if (this._postEnabled) {
      this.post = new PostStack(ctx.renderer, ctx.config.quality.level || 'high',
        q.get('msaa') != null ? Number(q.get('msaa')) : null);
      this.post.setSize(ctx.viewport.width, ctx.viewport.height);
    }

    // ── weather ──────────────────────────────────────────────────────────────────────
    this.precip = new Precipitation(rng.fork('precip'), ctx.materials, ctx.config.quality.particleScale || 1);
    ctx.scene.add(this.precip.group);

    // ── street-lamp light pools ──────────────────────────────────────────────────────
    // The whole city's street lighting, as additive geometry rather than sixty punctual
    // lights. See lamps.js for why that is the only affordable shape for it (§4.9d).
    this.lampPools = new LampPools(ctx);
    ctx.scene.add(this.lampPools.group);

    this._offWeather = ctx.bus.on(EV.WORLD_WEATHER_CHANGED, (p) => {
      this.setWeather(p && p.weather, p && p.transitionMs);
    });

    // Seed from whatever gameplay already thinks the weather is.
    const startWeather = ctx.gameplay && ctx.gameplay.weather ? ctx.gameplay.weather : 'thaw';
    this.weather = WEATHER_PROFILES[startWeather] ? startWeather : 'thaw';
    this.weatherFrom = this.weather;
    this.transitionDur = 0;
    this._applyProfile(true);

    // ── environment (IBL) ────────────────────────────────────────────────────────────
    this._pmrem = new THREE.PMREMGenerator(ctx.renderer);
    ctx.assets.register(Promise.resolve().then(() => this._rebuildEnv(true)));

    // ── fallback lighting stage ──────────────────────────────────────────────────────
    if (L.fallbackGround.enabled && ctx.world && ctx.world.isFallback) {
      this.fallbackStage = new FallbackStage(ctx, rng.fork('stage'));
      ctx.scene.add(this.fallbackStage.build());
    }

    // ── publish the interface ────────────────────────────────────────────────────────
    const self = this;
    ctx.look = {
      /** Current weather id (the one being faded TOWARD). */
      get weather() { return self.weather; },
      /** 0..1 cross-fade progress. 1 = settled. */
      get blend() { return self._blend01(); },
      /** 0..1 how wet every surface is. `fx` should scale spray/splash by this. */
      get wetness() { return self.profile.wetness; },
      /** 0..1 snow cover on horizontal surfaces. `world` may use it to sit snowbanks. */
      get snowCover() { return self.profile.snowCover; },
      /** 0..1 night factor. Emissives, headlights and neon scale off this. */
      get night() { return self.profile.night; },
      /** Unit vector pointing FROM the scene TOWARD the sun. Read-only. */
      sunDirection: this.sunDirection,
      /** The fog / horizon colour, for anything that wants to fade into the distance. */
      get fogColor() { return self.ctx.scene.fog.color; },
      /** Hand precipitation ownership to `fx` if it wants it. */
      setPrecipitation(on) { if (self.precip) self.precip.setEnabled(on); },
      /** Force an environment-map rebuild (e.g. after a big lighting change). */
      requestEnvUpdate() { self._rebuildEnv(true); },
      /** Shared shader uniform bag — the same object as ctx.materials.uniforms(). */
      uniforms: ctx.materials.uniforms(),
      isFallback: false,
    };

    // ── program-count discipline (ARCHITECTURE.md §9: ≤ 45 shader programs) ──────────
    // three bakes the OUTPUT COLOUR SPACE into the program cache key. Core calls
    // `renderer.compileAsync(scene, camera)` at the end of boot, and whatever render target
    // happens to be bound at that moment decides which variant gets compiled. If we leave
    // the canvas bound, every material in the game compiles TWICE — once as `srgb` for the
    // pre-warm and again as `srgb-linear` the first time we render into the post target.
    // Measured: 53 programs instead of 26, and ~25 extra seconds of SwiftShader link time.
    //
    // Binding the post scene target here makes the pre-warm compile exactly the variants
    // the real render path uses. `render()` always sets the target explicitly, so nothing
    // downstream depends on this staying bound.
    //
    // Other subsystems bake procedural textures into their own render targets during init
    // and during `settleAssets()`, and every one of them restores the target to whatever
    // was bound when THEY started. So re-assert the binding across several settle waves —
    // `settleAssets()` drains in waves and picks up anything registered during a wave, so
    // chaining a few hops puts us last, immediately before core's `compileAsync`.
    this._bindForPrewarm(5);
  }

  /** Re-assert the pre-warm render target across `n` settleAssets waves. See init(). */
  _bindForPrewarm(n) {
    if (!this._postEnabled || !this.post) return;
    const ctx = this.ctx;
    ctx.renderer.setRenderTarget(this.post.sceneTarget);
    if (n <= 0) return;
    ctx.assets.register(Promise.resolve().then(() => this._bindForPrewarm(n - 1)));
  }

  _configureRenderer() {
    const r = this.ctx.renderer;
    const q = this.ctx.config.quality;

    // Colour space: the renderer works in linear and encodes to sRGB on output. Every hex
    // in palette.js is therefore interpreted as sRGB and converted for us.
    r.outputColorSpace = THREE.SRGBColorSpace;

    // When post is on we render the scene LINEAR into an HDR target and tonemap in the
    // composite pass; when it is off the renderer applies the same curve directly.
    r.toneMapping = THREE.NeutralToneMapping;
    r.toneMappingExposure = L.renderer.exposure;

    r.shadowMap.enabled = q.shadows !== false;
    /**
     * ⚠ PCFShadowMap, AND DO NOT SET `PCFSoftShadowMap` HERE — IT IS DEPRECATED IN three r185
     * AND SETTING IT COSTS 31 SHADER PROGRAMS FOR NOTHING.
     *
     * Wave 8 set `PCFSoftShadowMap` to answer `cone-slalom`'s "essentially no penumbra". It
     * never took effect, and it broke the program budget. `WebGLShadowMap.render()` in r185:
     *
     *     if ( this.type === PCFSoftShadowMap ) {
     *       warn( 'WebGLShadowMap: PCFSoftShadowMap has been deprecated. Using PCFShadowMap
     *              instead.' );
     *       this.type = PCFShadowMap;
     *     }
     *
     * It downgrades on the FIRST shadow render — but `renderer.compileAsync()` has already
     * pre-warmed every material by then, and `shadowMapType` is in three's program cache key.
     * So every affected material compiles ONCE at PCFSoftShadowMap for the pre-warm and again
     * at PCFShadowMap for the real frame. MEASURED, two builds differing only in this line:
     *
     *     PCFSoftShadowMap   74 programs, 37 distinct, 37 wasted   ✗ (budget 45)
     *     PCFShadowMap       43 programs, 32 distinct, 11 wasted   ✓
     *
     * That is half the program-budget breach, from one line that had no visual effect at all —
     * the shadow map has been PCF the whole time, in both builds.
     *
     * The critic's finding is therefore STILL OPEN and the wave-8 "fixed it" claim is
     * withdrawn. If someone wants a real penumbra, the levers that exist in r185 are VSM
     * (a different cost and quality profile, and it needs its own blur pass) or a coarser
     * shadow texel via `shadowExtent` — and `shadowNormalBias` must be re-derived with it,
     * because over-biasing erases a cone's contact entirely (see `renderer.shadowNormalBias`).
     * Whatever is tried, MEASURE THE EDGE BEFORE AND AFTER; the withdrawn claim was an
     * assertion about the "before" that was never measured.
     */
    r.shadowMap.type = THREE.PCFShadowMap;
    r.shadowMap.autoUpdate = true;

    r.setClearColor(0x8DC3FC, 1);
    r.autoClear = true;
    r.info.autoReset = false;   // core owns the reset; do not fight it
  }

  _buildLights() {
    const ctx = this.ctx;
    const q = ctx.config.quality;
    const size = q.level === 'medium' ? L.renderer.shadowMapSizeMedium : L.renderer.shadowMapSize;

    // ── key: the sun ─────────────────────────────────────────────────────────────────
    const sun = new THREE.DirectionalLight(0xFFF2D6, L.light.sun.thaw.intensity);
    sun.name = 'look.sun';
    sun.castShadow = q.shadows !== false;
    sun.shadow.mapSize.set(size, size);
    const e = L.renderer.shadowExtent;
    sun.shadow.camera.left = -e;
    sun.shadow.camera.right = e;
    sun.shadow.camera.top = e;
    sun.shadow.camera.bottom = -e;
    sun.shadow.camera.near = L.renderer.shadowNear;
    sun.shadow.camera.far = L.renderer.shadowFar;
    sun.shadow.bias = L.renderer.shadowBias;
    sun.shadow.normalBias = L.renderer.shadowNormalBias;
    sun.target.position.set(0, 0, 0);
    ctx.scene.add(sun, sun.target);
    this.sun = sun;

    // ── bounce fill from roughly opposite the sun ────────────────────────────────────
    // Without this, every shadow side of every building falls to hemisphere-only and the
    // facades read as flat grey cards. With it they keep their form.
    const fill = new THREE.DirectionalLight(LIGHT.fill.thaw, L.light.fill.thaw);
    fill.name = 'look.fill';
    fill.castShadow = false;
    ctx.scene.add(fill, fill.target);
    this.fill = fill;

    // ── sky/ground ambient ───────────────────────────────────────────────────────────
    const hemi = new THREE.HemisphereLight(LIGHT.hemiSky.thaw, LIGHT.hemiGround.thaw, L.light.hemi.thaw);
    hemi.name = 'look.hemi';
    ctx.scene.add(hemi);
    this.hemi = hemi;

    // ── night: ONE sodium fixture, standing on the kerb ──────────────────────────────
    //
    // Round 6 hung this light above and behind the CAMERA. A light that travels with the
    // camera cannot make a pool — the same surface is lit by the same amount no matter how
    // far you drive — so what it produced was a flat global amber tint, which is exactly
    // what a critic reported: "not one fixture with a halo and a falloff pool". It now sits
    // on the kerb line a fixed distance down the road (see `_placeLamp`), so the road slides
    // THROUGH the pool and one continuous surface carries the whole reference §9.1 ramp:
    // near-white core → #C89F62 on the kerb → #946F36 → dark.
    //
    // ⚠ EXACTLY ONE. Round 7 first shipped this as a pair of fixtures (sodium + a cooler LED
    // on the far kerb, for criterion 7) and NUM_POINT_LIGHTS 1 → 3 cost **p95 8.3 ms → 23.6 ms**
    // measured on an M2 at 1920×1080 — a forward renderer evaluates every punctual light on
    // every fragment of every surface in the frame, and this scene has a lot of both. The
    // budget for the whole frame is 14 ms. If you want a second fixture, it has to be a
    // ground decal or a baked emissive, not another light.
    //
    // The colour is the MID-RAMP (#F0C182), not the sampled halo core (#F4E3BF): the core is
    // near-white because it is blown out, and using it as the source put near-neutral light
    // on everything in the pool so surfaces kept their own grey. §9.1's defining property is
    // that they lose it. Authoring the light amber and letting the tonemap blow the core to
    // white reproduces both ends of the ramp from one source.
    const sodium = new THREE.PointLight(LIGHT.sodiumLamp, 0, L.light.lamp.distance, L.light.lamp.decay);
    sodium.name = 'look.sodium';
    sodium.castShadow = false;
    ctx.scene.add(sodium);
    this.sodium = sodium;

    // ── night: the player's headlights ───────────────────────────────────────────────
    // COOL WHITE, deliberately. Reference §9.2 asks for sodium and a cooler source in the
    // same frame and calls that contrast a Montreal signature; round 8 measured this frame at
    // 90.2% warm against 1.1% cool. A works truck's LED headlamps are ~5000 K, so the one
    // large cool pool in the frame is physically the truck's own beam — and it lands in the
    // middle of the composition, where the eye already is.
    const head = new THREE.SpotLight(0xDCE8FF, 0, 80, 0.72, 0.58, 1.35);
    head.name = 'look.headlights';
    head.castShadow = false;
    ctx.scene.add(head, head.target);
    this.headlights = head;
  }

  /* ═════════════════════════════════════════════════════════════════════ weather ══ */

  /**
   * Start a cross-fade to `weather`. Called from `world:weatherChanged` and the harness.
   * Safe to call with the current weather.
   */
  setWeather(weather, transitionMs) {
    if (!weather || !WEATHER_PROFILES[weather]) return;
    if (weather === this.weather && this.transitionDur === 0) return;

    // Snapshot the CURRENT blended look as the "from" end so an interrupted fade never jumps.
    this.weatherFrom = this._blend01() < 1 ? this._snapshotFrom() : this.weather;
    this.weather = weather;
    const ms = transitionMs != null ? transitionMs : L.weather.defaultTransitionMs;
    this.transitionDur = this._frozen ? 0 : Math.max(0, ms / 1000);
    this.transitionStart = this.ctx.time.sim;
    this._applyProfile(this.transitionDur === 0);
  }

  /** Freeze the in-flight blend into a private profile so we can fade from mid-transition. */
  _snapshotFrom() {
    if (!this._fromProfile) this._fromProfile = makeBlendTarget();
    copyProfile(this.profile, this._fromProfile);
    this._fromProfile.id = '_mid';
    return '_mid';
  }

  _blend01() {
    if (this.transitionDur <= 0) return 1;
    return clamp01((this.ctx.time.sim - this.transitionStart) / this.transitionDur);
  }

  _profileById(id) {
    if (id === '_mid' && this._fromProfile) return this._fromProfile;
    return WEATHER_PROFILES[id] || WEATHER_PROFILES.thaw;
  }

  /** Blend the two endpoint profiles and push the result into every consumer. */
  _applyProfile(force) {
    const t = smoothstep(this._blend01());
    const a = this._profileById(this.weatherFrom);
    const b = this._profileById(this.weather);
    if (t >= 1) { copyProfile(b, this.profile); this.profile.id = this.weather; }
    else blendProfiles(a, b, t, this.profile);

    const p = this.profile;
    const ctx = this.ctx;

    // ── sun direction ────────────────────────────────────────────────────────────────
    const el = p.sunElevation * DEG, az = p.sunAzimuth * DEG;
    this.sunDirection.set(
      Math.cos(el) * Math.sin(az),
      Math.sin(el),
      Math.cos(el) * Math.cos(az)
    ).normalize();

    // ── sky ──────────────────────────────────────────────────────────────────────────
    const u = this.sky.uniforms;
    u.uZenith.value.copy(p.zenith);
    u.uMid.value.copy(p.mid);
    u.uHorizon.value.copy(p.horizon);
    u.uHaze.value.copy(p.haze);
    u.uGround.value.copy(p.ground);
    u.uCloud.value.copy(p.cloud);
    u.uCloudShade.value.copy(p.cloudShade);
    u.uSunColor.value.copy(p.skySun);
    u.uGradientPower.value = p.gradientPower;
    u.uHorizonSoft.value = p.horizonSoft;
    u.uGain.value = p.skyGain;
    u.uSunSize.value = p.sunSize;
    u.uSunGlowPower.value = p.sunGlowPower;
    u.uSunGlowStrength.value = p.sunGlowStrength;
    u.uCloudAmount.value = p.cloudAmount;
    u.uCloudSharp.value = p.cloudSharp;
    u.uCloudScale.value = p.cloudScale;
    u.uCloudOpacity.value = p.cloudOpacity;
    this.sky.setSunDirection(this.sunDirection);

    // ── lights ───────────────────────────────────────────────────────────────────────
    this.sun.color.copy(p.sunColor);
    this.sun.intensity = p.sunIntensity;
    this.fill.color.copy(p.fillColor);
    this.fill.intensity = p.fillIntensity;
    this.hemi.color.copy(p.hemiSky);
    this.hemi.groundColor.copy(p.hemiGround);
    this.hemi.intensity = p.hemiIntensity;
    this.sodium.intensity = L.light.lamp.intensity * p.night;
    this.headlights.intensity = 340 * p.night;
    ctx.scene.environmentIntensity = p.envIntensity;

    // ── fog ──────────────────────────────────────────────────────────────────────────
    // Fog colour is the horizon haze, so the far city dissolves INTO the sky rather than
    // into an arbitrary grey. The road stays readable because `near` is a long way out.
    const fog = ctx.scene.fog;
    this._scratchC.copy(p.haze).lerp(p.horizon, 1 - L.fog.horizonBlend).multiplyScalar(p.skyGain);
    fog.color.copy(this._scratchC);
    fog.near = p.fogNear;
    fog.far = p.fogFar;
    ctx.renderer.setClearColor(this._scratchC, 1);

    // ── materials ────────────────────────────────────────────────────────────────────
    ctx.materials.applyLook({
      weather: p.id === '_mid' ? this.weather : p.id,
      wetness: p.wetness,
      snow: p.snowCover,
      night: p.night,
      fogColor: fog.color,
      sunDir: this.sunDirection,
      sunColor: p.sunColor,
    });

    // ── post ─────────────────────────────────────────────────────────────────────────
    if (this.post) this.post.applyProfile(p, this._exposure);
    ctx.renderer.toneMappingExposure = this._postEnabled ? 1.0 : this._exposure;

    if (force) this._rebuildEnv(true);
  }

  /* ═════════════════════════════════════════════════════════════════ environment ══ */

  /**
   * Bake the sky dome into a PMREM environment map. This is what makes a wet road at night
   * actually reflect the sodium sky instead of just going shiny-grey, and it is the cheapest
   * possible IBL — six faces of a shader that is already compiled.
   *
   * Rebuilt only when the blend crosses a quantised step, so a 1.6 s cross-fade costs a
   * handful of bakes rather than ninety-six.
   */
  _rebuildEnv(force) {
    if (!this._pmrem) return;
    const t = this._blend01();
    const key = `${this.weatherFrom}>${this.weather}@${Math.round(t * 4)}`;
    if (!force && key === this._envKey) return;
    this._envKey = key;

    try {
      const prev = this._envRT;
      // The env scene holds only the sky dome — never the city.
      this._envRT = this._pmrem.fromScene(this.sky.envScene, 0.04, 1, 2000);
      this.ctx.scene.environment = this._envRT.texture;
      if (prev) prev.dispose();
      this._envBuilds++;
    } catch (err) {
      console.warn('[look] environment bake failed; continuing without IBL', err);
      this.ctx.scene.environment = null;
    }
  }

  /* ════════════════════════════════════════════════════════════════════════ loop ══ */

  fixedUpdate(/* dt */) {
    // Nothing. `look` is presentation only — it must never mutate simulation state.
  }

  /**
   * Presentation. Everything here reads ctx.time.sim, ctx.camera and ctx.player, all of
   * which are deterministic, so a frozen shot reproduces exactly.
   */
  update(/* dt, alpha */) {
    const ctx = this.ctx;
    const sim = ctx.time.sim;
    const camera = ctx.camera;

    // Weather cross-fade.
    if (this.transitionDur > 0) {
      const done = this._blend01() >= 1;
      this._applyProfile(false);
      if (done) {
        this.transitionDur = 0;
        this.weatherFrom = this.weather;
        this._rebuildEnv(true);
      } else {
        this._rebuildEnv(false);
      }
    }

    // Sky follows the camera and drifts on sim time.
    this.sky.followCamera(camera);
    this.sky.setTime(sim);
    ctx.materials.tick(sim);

    // ── shadow frustum tracking ──────────────────────────────────────────────────────
    // Push the focus ahead of the camera so the shadow budget is spent on what you are
    // driving into, and snap it to the shadow map's texel grid so edges do not crawl.
    const focus = this._shadowFocus;
    camera.getWorldDirection(this._scratchV);
    focus.copy(camera.position).addScaledVector(this._scratchV, L.renderer.shadowLead);
    focus.y = 0;
    if (L.renderer.shadowTexelSnap) {
      const texel = (L.renderer.shadowExtent * 2) / this.sun.shadow.mapSize.x;
      const step = texel * 4;
      focus.x = Math.round(focus.x / step) * step;
      focus.z = Math.round(focus.z / step) * step;
    }
    this.sun.target.position.copy(focus);
    this.sun.position.copy(focus).addScaledVector(this.sunDirection, 120);
    this.sun.target.updateMatrixWorld();
    this.sun.updateMatrixWorld();

    this._scratchV2.set(-this.sunDirection.x, 0, -this.sunDirection.z);
    if (this._scratchV2.lengthSq() < 1e-6) this._scratchV2.set(0, 0, 1);
    this._scratchV2.normalize();
    this.fill.target.position.copy(focus);
    this.fill.position.copy(focus).addScaledVector(this._scratchV2, 90);
    this.fill.position.y = focus.y + 90 * Math.sin(L.light.fillElevation * DEG);
    this.fill.target.updateMatrixWorld();
    this.fill.updateMatrixWorld();

    // ── night lighting rides with the player ─────────────────────────────────────────
    const night = this.profile.night;
    if (night > 0.01) {
      const p = ctx.player.position;
      const fwd = ctx.player.forward;
      this._placeLamp(this.sodium, L.light.lamp, p, fwd);
      this.headlights.position.set(p.x + fwd.x * 1.6, p.y + 1.15, p.z + fwd.z * 1.6);
      this.headlights.target.position.set(p.x + fwd.x * 26, p.y - 1.2, p.z + fwd.z * 26);
      this.headlights.target.updateMatrixWorld();
    }

    // ── depth-reading post effects need the projection every frame ───────────────────
    // Sky occlusion runs in every weather, so the projection constants are unconditional.
    // The GROUND height is not: `_groundY` calls into the world query, which returns a fresh
    // object, and a dry frame must not pay an allocation per frame for a term it multiplies
    // by zero (N9).
    if (this.post) {
      this.post.setCamera(camera);
      this._driveMotionBlur(camera);
      if (this.profile.wetStreak > 0.001) {
        this.post.setHorizon(this._horizonV(camera));
        this.post.setGroundY(this._groundY());
      }
    }

    // ── precipitation ────────────────────────────────────────────────────────────────
    if (this.precip) this.precip.update(sim, camera, this.profile.rain, this.profile.snow);

    // ── street-lamp pools ────────────────────────────────────────────────────────────
    // Rebuilt from world's live luminaire instances, so the pools follow block streaming.
    // A no-op the moment `night` is zero, which is every daylight frame.
    if (this.lampPools) this.lampPools.sync(night, this.profile.rain, camera, this.profile.wetness);

    // ── retire the fallback stage the moment `world` publishes a real query ──────────
    if (this.fallbackStage && ctx.world && !ctx.world.isFallback) {
      if (this.lampPools) this.lampPools.invalidate();
      ctx.scene.remove(this.fallbackStage.group);
      this.fallbackStage.dispose();
      this.fallbackStage = null;
    }
  }

  /**
   * Put a night fixture ON THE KERB, `spec.ahead` metres down the road from the player.
   *
   * A street lamp that follows the camera produces an even amber tint and no pool at all,
   * which is precisely what a critic measured last round. Anchoring to
   * `world.nearestRoadPoint` + the road's own perpendicular means the pool stays put in world
   * space while the road slides through it, so one surface carries the whole §9.1 ramp:
   * near-white core → #C89F62 on the kerb → #946F36 → dark.
   *
   * Pure function of (player pose, world query), both deterministic, so a frozen shot
   * reproduces exactly (§7.3). Falls back to a plain lateral offset if `world` has no query.
   */
  _placeLamp(light, spec, p, fwd) {
    const w = this.ctx.world;
    let cx = p.x + fwd.x * spec.ahead;
    let cz = p.z + fwd.z * spec.ahead;
    let px = -fwd.z, pz = fwd.x;          // perpendicular to travel, fallback
    let half = 5.5;

    if (w && typeof w.nearestRoadPoint === 'function') {
      const np = w.nearestRoadPoint(cx, cz);
      if (np && Number.isFinite(np.x) && Number.isFinite(np.z)) { cx = np.x; cz = np.z; }
      const r = typeof w.roadAt === 'function' ? w.roadAt(cx, cz) : null;
      if (r) {
        if (r.width > 0) half = r.width * 0.5;
        if (r.dir) { px = -r.dir.y; pz = r.dir.x; }
      }
    }
    const out = (half + spec.kerbOut) * spec.side;
    light.position.set(cx + px * out, (p.y || 0) + spec.height, cz + pz * out);
  }

  /**
   * ─────────────────────────────── CAMERA MOTION BLUR (W10c) ──────────────────────────
   *
   * Resolve the camera's translation into its own VIEW basis and hand post one strength
   * scalar. See `src/render/tuning.js` post.motionBlur for why this is the right operator
   * and why it holds the near field.
   *
   * DETERMINISM (§7.3). The velocity is NOT differenced across frames — a frame-history
   * term would make `harness.render()` depend on how many frames preceded it and break
   * bit-reproducibility. It is read straight off `ctx.player`, which is fixed-step sim
   * state, and projected through the camera's current world matrix. Both are deterministic,
   * so a frozen shot reproduces exactly.
   *
   * OFF when the harness has detached the camera (`wide-establish`, `pothole-fill`): the
   * player may be moving there but the CAMERA is pinned, and it is the camera's motion that
   * smears the frame. Allocation-free — two scratch vectors, reused (N9).
   */
  _driveMotionBlur(camera) {
    const M = L.post.motionBlur;
    const p = this.ctx.player;
    const shared = this.ctx.materials.uniforms();
    if (!M.enabled || !p || (camera.userData && camera.userData.harnessDetached)) {
      this.post.setMotion(0, 0, 0, 0);
      if (shared.uRainVel) shared.uRainVel.value.set(0, 0, 0);
      return;
    }

    // World-space camera velocity ≈ the chase rig's target velocity. `velocity` is the
    // published vector; fall back to speed × forward, which `setState({playerPose})` sets
    // even when the physics step has not run.
    const v = this._scratchV;
    if (p.velocity && Number.isFinite(p.velocity.x)) v.copy(p.velocity);
    else if (p.forward) v.copy(p.forward).multiplyScalar(p.speed || 0);
    else v.set(0, 0, 0);

    const speed = v.length();
    if (speed < M.speedMin) {
      this.post.setMotion(0, 0, 0, 0);
      if (shared.uRainVel) shared.uRainVel.value.set(0, 0, 0);
      return;
    }

    const ramp = clamp01((speed - M.speedMin) / Math.max(1e-3, M.speedFull - M.speedMin));
    const wScale = M.scale[this.profile.id] != null ? M.scale[this.profile.id] : M.scale.thaw;

    // World → view. The camera's world matrix is orthonormal in its upper 3×3, so the
    // inverse rotation is the transpose: dot the velocity with each basis column.
    const e = camera.matrixWorld.elements;
    const vx = v.x * e[0] + v.y * e[1] + v.z * e[2];
    const vy = v.x * e[4] + v.y * e[5] + v.z * e[6];
    const vz = v.x * e[8] + v.y * e[9] + v.z * e[10];
    this.post.setMotion(vx, vy, vz, M.shutter * ramp * wScale);
    // Same view-space velocity drives the rain-streak shear (materials `_rainShear`). One
    // derivation, two consumers — a windscreen and a lens see the same apparent motion.
    this.ctx.materials.uniforms().uRainVel.value.set(vx, vy, vz);
  }

  /**
   * Screen-space v of the horizon for the wet-road reflection gate. For a camera pitched
   * down by θ with vertical fov f, a ray in the horizontal plane lands at
   * ndc_y = tan(θ) / tan(f/2), and v = (ndc_y + 1) / 2.
   */
  /**
   * Height of the carriageway under the player, so the reflection's ground test travels.
   *
   * CACHED ON SIM TIME. `world.sampleGround` returns a FRESH OBJECT per call, and this runs
   * once per frame in every wet weather — 60 short-lived objects a second for a number that
   * moves by centimetres. N9 says no per-frame allocation in a hot path, and heap growth is
   * the one profiler gate still failing. Re-sampling every 0.1 s of SIM time keeps it exact
   * to within a few centimetres at 90 km/h and keeps shots bit-reproducible, because
   * `ctx.time.sim` is the deterministic clock (§6) — a wall clock here would not be.
   */
  _groundY() {
    const sim = this.ctx.time.sim;
    if (sim - this._groundYAt < 0.1 && this._groundYAt > -1) return this._groundYCache;
    this._groundYAt = sim;
    const w = this.ctx.world;
    const p = this.ctx.player && this.ctx.player.position;
    if (!p || !w || typeof w.sampleGround !== 'function') { this._groundYCache = 0; return 0; }
    const g = w.sampleGround(p.x, p.z);
    this._groundYCache = g && Number.isFinite(g.y) ? g.y : 0;
    return this._groundYCache;
  }

  _horizonV(camera) {
    camera.getWorldDirection(this._scratchV);
    const flat = Math.sqrt(this._scratchV.x * this._scratchV.x + this._scratchV.z * this._scratchV.z);
    const tanPitch = flat > 1e-5 ? -this._scratchV.y / flat : 0;
    const tanHalfFov = Math.tan((camera.fov || 60) * 0.5 * DEG);
    return clamp01(0.5 + 0.5 * tanPitch / Math.max(tanHalfFov, 1e-4));
  }

  /**
   * THE ONLY PLACE RENDERING HAPPENS (ARCHITECTURE.md §2, frame order step 10).
   */
  render() {
    const ctx = this.ctx;
    const r = ctx.renderer;

    if (this._postEnabled && this.post) {
      // Scene goes into the HDR target with tonemapping OFF — the composite pass owns it.
      r.toneMapping = THREE.NoToneMapping;
      r.toneMappingExposure = 1.0;
      r.setRenderTarget(this.post.sceneTarget);
      r.clear();
      r.render(ctx.scene, ctx.camera);
      r.setRenderTarget(null);
      this.post.run();
      r.toneMapping = THREE.NeutralToneMapping;
      // Leave the HDR target bound. `PPM.harness.settleAssets()` can be called at any point
      // after boot and re-runs `renderer.compileAsync(scene, camera)`; whatever target is
      // bound at that moment decides which output-colour-space variant of every shader gets
      // compiled. Leaving the canvas bound made the harness compile a SECOND copy of every
      // program in the game (measured: 83 → 45). The canvas has already received the
      // composited frame by this point, so this costs nothing.
      r.setRenderTarget(this.post.sceneTarget);
    } else {
      r.toneMapping = THREE.NeutralToneMapping;
      r.toneMappingExposure = this._exposure;
      r.setRenderTarget(null);
      r.render(ctx.scene, ctx.camera);
    }
  }

  onResize(width, height) {
    if (this.post) this.post.setSize(width, height);
  }

  /* ═════════════════════════════════════════════════════════════════════ harness ══ */

  /** ARCHITECTURE.md §7.1. Apply only the keys `look` owns. */
  applyHarnessState(patch) {
    if (!patch) return;
    if (patch.weather && WEATHER_PROFILES[patch.weather]) {
      // Shots must settle instantly — a shot is a single frame, not a fade.
      this.weatherFrom = patch.weather;
      this.weather = patch.weather;
      this.transitionDur = 0;
      this.transitionStart = this.ctx.time.sim;
      this._applyProfile(true);
    }
    if (patch.timeOfDay != null) {
      this.timeOfDay = patch.timeOfDay;
      // timeOfDay biases sun elevation within the current weather: 0.5 = the profile's own
      // elevation, 0/1 = the horizon. Lets a shot ask for a late-afternoon rake without
      // inventing a new weather profile.
      const base = this._profileById(this.weather).sunElevation;
      const k = 1 - Math.abs(patch.timeOfDay - 0.5) * 2;
      this.profile.sunElevation = 3 + (base - 3) * clamp01(k * 1.35);
      const el = this.profile.sunElevation * DEG, az = this.profile.sunAzimuth * DEG;
      this.sunDirection.set(Math.cos(el) * Math.sin(az), Math.sin(el), Math.cos(el) * Math.cos(az)).normalize();
      this.sky.setSunDirection(this.sunDirection);
      this._rebuildEnv(true);
    }
    if (patch.exposure != null) {
      this._exposure = patch.exposure;
      this._applyProfile(false);
    }
    if (patch.post != null) this._postEnabled = !!patch.post && !!this.post;
  }

  harnessStats() {
    const p = this.profile;
    return {
      weather: this.weather,
      blend: +this._blend01().toFixed(3),
      wetness: +p.wetness.toFixed(3),
      snow: +p.snowCover.toFixed(3),
      night: +p.night.toFixed(3),
      sunElevation: +p.sunElevation.toFixed(2),
      sunIntensity: +p.sunIntensity.toFixed(3),
      exposure: +this._exposure.toFixed(3),
      fogNear: Math.round(p.fogNear),
      fogFar: Math.round(p.fogFar),
      post: this._postEnabled,
      postStats: this.post ? this.post.stats() : null,
      envBuilds: this._envBuilds,
      precip: this.precip ? this.precip.stats() : null,
      lampPools: this.lampPools ? this.lampPools.stats() : null,
      shadows: this.ctx.renderer.shadowMap.enabled,
      fallbackStage: !!this.fallbackStage,
      tonemap: 'pbr-neutral',
    };
  }

  /**
   * Pin every presentation clock to sim time. Everything in `look` already derives from
   * ctx.time.sim, so the only thing left to do is collapse an in-flight weather fade — a
   * shot is one frame and must not depend on how many frames the harness happened to step.
   */
  freezeClocks() {
    this._frozen = true;
    if (this.transitionDur > 0) {
      this.transitionDur = 0;
      this.weatherFrom = this.weather;
      this._applyProfile(true);
    }
  }

  async reset(opts = {}) {
    this._frozen = false;
    this._groundYAt = -2;
    this.timeOfDay = 0.42;
    this._exposure = L.renderer.exposure;
    const w = opts.weather && WEATHER_PROFILES[opts.weather] ? opts.weather : 'thaw';
    this.weather = w;
    this.weatherFrom = w;
    this.transitionDur = 0;
    this.transitionStart = 0;
    this._applyProfile(true);
    if (this.precip) this.precip.setEnabled(true);
    // `world` rebuilds its props on reset, so every cached luminaire handle is stale.
    if (this.lampPools) this.lampPools.invalidate();
  }

  dispose() {
    if (this._offWeather) { this._offWeather(); this._offWeather = null; }
    if (this.sky) { this.ctx.scene.remove(this.sky.mesh); this.sky.dispose(); this.sky = null; }
    if (this.post) { this.post.dispose(); this.post = null; }
    if (this.precip) { this.ctx.scene.remove(this.precip.group); this.precip.dispose(); this.precip = null; }
    if (this.lampPools) { this.ctx.scene.remove(this.lampPools.group); this.lampPools.dispose(); this.lampPools = null; }
    if (this.fallbackStage) { this.ctx.scene.remove(this.fallbackStage.group); this.fallbackStage.dispose(); this.fallbackStage = null; }
    if (this._envRT) { this._envRT.dispose(); this._envRT = null; }
    if (this._pmrem) { this._pmrem.dispose(); this._pmrem = null; }
    for (const l of [this.sun, this.fill, this.hemi, this.sodium, this.headlights]) {
      if (!l) continue;
      this.ctx.scene.remove(l);
      if (l.target) this.ctx.scene.remove(l.target);
      if (l.dispose) l.dispose();
    }
    this.sun = this.fill = this.hemi = this.sodium = this.headlights = null;
  }
}

/* ══════════════════════════════════════════════════════════════════════════ utils ══ */

function copyProfile(src, dst) {
  for (const k in src) {
    const v = src[k];
    if (v && v.isColor) dst[k].copy(v);
    else if (v && v.isVector3) dst[k].copy(v);
    else dst[k] = v;
  }
  return dst;
}

export default LookSystem;
